import {
  CategorySchema,
  CreateCategoryRequest,
  UpdateCategorySchema,
} from '@act/shared/data-transfer-objects';
import { FormBuilder } from '@act/storybook';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo } from 'react';
import useUnsavedChangesDialog from 'react-components/dialog-forms/hooks/use-unsaved-changes-dialog';
import { useForm } from 'react-hook-form';
import {
  createNeedsMetadataSuccess,
  updateNeedsMetadataSuccess,
} from 'redux/slices/metaSlice';
import { metaSliceSelectors } from 'redux/slices/metaSlice.hooks';
import { AdminDrawerForm } from '../../AdminDrawer';
import { needRequests } from 'redux/requests/need.requests';
import { useAppDispatch } from 'redux/store-hooks';
import { useApiCall } from 'redux/utils/api-hook';
import { FormConfig } from 'stories/forms/FormBuilder/FormBuilder.types';
import { z } from 'zod';
import { useToast } from '../../../../app/ReusableToast';

export const NeedsCategoriesCreateEdit = ({
  open,
  setOpen,
  isEdit,
  selectedCategory,
}) => {
  const dispatch = useAppDispatch();
  const toast = useToast();
  const handleUnsavedChanges = useUnsavedChangesDialog();
  const schema = isEdit ? UpdateCategorySchema : CategorySchema;
  type CategoryFormData = z.infer<typeof schema>;
  const { control, handleSubmit, formState } = useForm<CategoryFormData>({
    defaultValues: selectedCategory || {
      label: '',
      key: '',
      domainId: undefined,
    },
    resolver: zodResolver(schema),
  });
  const [needCategoryResult, createNeedCategoryLoading, createNeedCategory] =
    useApiCall(needRequests.createNeedsMetadata);
  const [
    updateNeedCategoryResult,
    updateNeedCategoryLoading,
    updateNeedCategory,
  ] = useApiCall(needRequests.updateNeedsMetadata);
  const needs = metaSliceSelectors.useSelectNeedsMetadata().data;
  const domainOptions = needs.domains.map(domain => ({
    value: domain.id,
    label: domain.label,
  }));
  const formConfig: FormConfig<CategoryFormData> = [
    {
      type: 'input',
      name: 'label',
      label: 'Category Label',
      input: 'text',
    },
    {
      type: 'input',
      name: 'domainId',
      label: 'Domain',
      input: 'select',
      props: {
        options: domainOptions,
        fullWidth: true,
      },
    },
    {
      type: 'input',
      name: 'key',
      label: 'Key',
      input: 'text',
    },
  ];

  const onSubmit = (data: CategoryFormData) => {
    if (isEdit) {
      void updateNeedCategory({
        body: {
          ...data,
        },
        params: { type: 'categories', id: selectedCategory?.id },
      });
    } else {
      void createNeedCategory({
        body: {
          ...(data as CreateCategoryRequest),
        },
        params: { type: 'categories' },
      });
    }
  };

  // Handle successful creation
  useEffect(() => {
    if (needCategoryResult && !createNeedCategoryLoading && open) {
      dispatch(
        createNeedsMetadataSuccess({
          data: needCategoryResult,
          type: 'categories',
        })
      );
      toast('Category created successfully');
      setOpen(false);
    }
  }, [needCategoryResult, createNeedCategoryLoading]);

  // Handle successful update
  useEffect(() => {
    if (updateNeedCategoryResult && !updateNeedCategoryLoading && open) {
      dispatch(
        updateNeedsMetadataSuccess({
          data: updateNeedCategoryResult,
          type: 'categories',
        })
      );
      toast('Category updated successfully');
      setOpen(false);
    }
  }, [updateNeedCategoryResult, updateNeedCategoryLoading]);

  const { isDirty } = formState;
  const handleClose = () => {
    if (isDirty) {
      void handleUnsavedChanges(() => {
        setOpen(false);
      });
    } else {
      setOpen(false);
    }
  };
  const DrawerContent = useMemo(() => {
    return (
      <FormBuilder
        config={formConfig}
        control={control}
        formState={formState}
      />
    );
  }, [control, formConfig, formState.errors]);

  return (
    <AdminDrawerForm
      title={isEdit ? `Edit Category` : `Add New Category`}
      children={DrawerContent}
      onSubmit={handleSubmit(onSubmit)}
      open={open}
      setOpen={handleClose}
    />
  );
};
